import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/axiosConfig';
import { ExternalLink, Loader2 } from 'lucide-react';

const STRAVA_DEV_URL = 'https://www.strava.com/settings/api';

const ServicesModal = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const [stravaClientId, setStravaClientId] = useState('');
  const [stravaClientSecret, setStravaClientSecret] = useState('');
  const [stravaRedirect, setStravaRedirect] = useState('');
  const [stravaLoading, setStravaLoading] = useState(false);
  const [stravaMsg, setStravaMsg] = useState(null);

  const [strydEmail, setStrydEmail] = useState('');
  const [strydPassword, setStrydPassword] = useState('');
  const [strydHint, setStrydHint] = useState(null);
  const [strydConnected, setStrydConnected] = useState(false);
  const [strydLoading, setStrydLoading] = useState(false);
  const [strydMsg, setStrydMsg] = useState(null);
  const [strydSyncLoading, setStrydSyncLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStravaMsg(null);
    setStrydMsg(null);
    setStrydPassword('');

    setStravaLoading(true);
    api
      .get('/api/strava/app-credentials')
      .then(({ data }) => {
        if (data?.clientId) setStravaClientId(data.clientId);
        setStravaRedirect(data?.redirectUri || '');
      })
      .catch(() => {})
      .finally(() => setStravaLoading(false));

    setStrydLoading(true);
    api
      .get('/api/stryd/credentials')
      .then(({ data }) => {
        setStrydConnected(!!data?.connected);
        setStrydHint(data?.emailHint || null);
      })
      .catch(() => {
        setStrydConnected(false);
        setStrydHint(null);
      })
      .finally(() => setStrydLoading(false));
  }, [isOpen]);

  const saveStrava = async () => {
    setStravaMsg(null);
    setStravaLoading(true);
    try {
      await api.put('/api/strava/app-credentials', {
        clientId: stravaClientId.trim(),
        clientSecret: stravaClientSecret,
      });
      setStravaMsg({ type: 'success', text: t('strava.appCredentialsSaved') });
      setStravaClientSecret('');
    } catch (err) {
      setStravaMsg({
        type: 'error',
        text: err.response?.data?.error || t('strava.appCredentialsSaveFailed'),
      });
    } finally {
      setStravaLoading(false);
    }
  };

  const saveStryd = async () => {
    setStrydMsg(null);
    if (!strydEmail.trim() || !strydPassword) {
      setStrydMsg({ type: 'error', text: t('services.strydValidation') });
      return;
    }
    setStrydLoading(true);
    try {
      await api.put('/api/stryd/credentials', {
        email: strydEmail.trim(),
        password: strydPassword,
      });
      setStrydConnected(true);
      const { data } = await api.get('/api/stryd/credentials');
      setStrydHint(data?.emailHint || null);
      setStrydPassword('');
      setStrydMsg({ type: 'success', text: t('services.strydSaved') });
    } catch (err) {
      setStrydMsg({
        type: 'error',
        text: err.response?.data?.error || t('services.strydSaveFailed'),
      });
    } finally {
      setStrydLoading(false);
    }
  };

  const disconnectStryd = async () => {
    setStrydMsg(null);
    setStrydLoading(true);
    try {
      await api.delete('/api/stryd/credentials');
      setStrydConnected(false);
      setStrydHint(null);
      setStrydMsg({ type: 'success', text: t('services.strydDisconnected') });
    } catch (err) {
      setStrydMsg({
        type: 'error',
        text: err.response?.data?.error || t('services.strydDisconnectFailed'),
      });
    } finally {
      setStrydLoading(false);
    }
  };

  const syncStrydActivities = async () => {
    setStrydMsg(null);
    setStrydSyncLoading(true);
    try {
      const { data } = await api.post('/api/stryd/sync/activities');
      const upserted = data?.upserted ?? 0;
      const stryd = data?.stryd_activity_count ?? 0;
      const matched = data?.matched ?? 0;
      const linked = data?.linked_exercises ?? 0;
      const imported = data?.citius_workouts_imported ?? 0;
      const note = Array.isArray(data?.errors) && data.errors[0] ? data.errors[0] : null;
      const shelfIds = data?.library_collection_workout_ids_on_shelf ?? 0;
      const shelfFetched = data?.library_collection_workouts_fetched ?? 0;
      const shelfFailed = data?.library_collection_workouts_fetch_failed ?? 0;
      const shelfSub =
        shelfIds > 0
          ? t('services.strydSyncActivitiesShelfNote', {
              shelfIds,
              shelfFetched,
              shelfFailed,
            })
          : null;
      const subParts = [
        note ? t('services.strydSyncActivitiesNote', { message: note }) : null,
        shelfSub,
      ].filter(Boolean);
      setStrydMsg({
        type: 'success',
        text: t('services.strydSyncActivitiesSuccess', { upserted, stryd, matched, linked, imported }),
        sub: subParts.length ? subParts.join(' ') : null,
      });
      window.dispatchEvent(new Event('trainingUpdated'));
    } catch (err) {
      setStrydMsg({
        type: 'error',
        text: err.response?.data?.error || t('services.strydSyncActivitiesFailed'),
      });
    } finally {
      setStrydSyncLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='max-w-[min(90vw,520px)]'>
        <DialogHeader>
          <DialogTitle>{t('services.title')}</DialogTitle>
          <DialogDescription>{t('services.description')}</DialogDescription>
        </DialogHeader>

        <div className='space-y-6 max-h-[min(70vh,560px)] overflow-y-auto pr-1'>
          <section className='space-y-3'>
            <h3 className='text-sm font-semibold text-foreground'>{t('services.stravaHeading')}</h3>
            <p className='text-xs text-muted-foreground'>{t('strava.appCredentialsIntro')}</p>
            <a
              href={STRAVA_DEV_URL}
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex items-center gap-1 text-xs text-primary hover:underline'
            >
              {t('services.stravaDeveloperLink')}
              <ExternalLink className='h-3 w-3' />
            </a>
            {!!stravaRedirect && (
              <div className='rounded-md border border-border bg-muted/40 p-2 text-xs'>
                <p className='font-medium text-foreground'>{t('strava.appCredentialsRedirectUri')}</p>
                <code className='mt-1 block break-all text-muted-foreground'>{stravaRedirect}</code>
                <p className='mt-1 text-muted-foreground'>{t('strava.appCredentialsRedirectUriHint')}</p>
              </div>
            )}
            <div className='space-y-2'>
              <Label htmlFor='svc-strava-client-id'>{t('strava.appCredentialsClientId')}</Label>
              <Input
                id='svc-strava-client-id'
                value={stravaClientId}
                onChange={(e) => setStravaClientId(e.target.value)}
                autoComplete='off'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='svc-strava-secret'>{t('strava.appCredentialsClientSecret')}</Label>
              <Input
                id='svc-strava-secret'
                type='password'
                value={stravaClientSecret}
                onChange={(e) => setStravaClientSecret(e.target.value)}
                autoComplete='new-password'
                placeholder={t('services.stravaSecretPlaceholder')}
              />
            </div>
            {stravaMsg && (
              <p
                className={
                  stravaMsg.type === 'success' ? 'text-sm text-primary' : 'text-sm text-destructive'
                }
              >
                {stravaMsg.text}
              </p>
            )}
            <Button type='button' onClick={saveStrava} disabled={stravaLoading}>
              {stravaLoading ? <Loader2 className='h-4 w-4 animate-spin' /> : t('strava.appCredentialsSave')}
            </Button>
          </section>

          <Separator />

          <section className='space-y-3'>
            <h3 className='text-sm font-semibold text-foreground'>{t('services.strydHeading')}</h3>
            <p className='text-xs text-muted-foreground'>{t('services.strydIntro')}</p>
            {strydConnected && strydHint && (
              <p className='text-xs text-muted-foreground'>
                {t('services.strydConnectedAs', { hint: strydHint })}
              </p>
            )}
            <div className='space-y-2'>
              <Label htmlFor='svc-stryd-email'>{t('services.strydEmail')}</Label>
              <Input
                id='svc-stryd-email'
                type='email'
                value={strydEmail}
                onChange={(e) => setStrydEmail(e.target.value)}
                autoComplete='email'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='svc-stryd-password'>{t('services.strydPassword')}</Label>
              <Input
                id='svc-stryd-password'
                type='password'
                value={strydPassword}
                onChange={(e) => setStrydPassword(e.target.value)}
                autoComplete='new-password'
              />
            </div>
            {strydMsg && (
              <div className='space-y-1'>
                <p
                  className={
                    strydMsg.type === 'success' ? 'text-sm text-primary' : 'text-sm text-destructive'
                  }
                >
                  {strydMsg.text}
                </p>
                {strydMsg.sub && (
                  <p className='text-xs text-muted-foreground'>{strydMsg.sub}</p>
                )}
              </div>
            )}
            <div className='flex flex-wrap gap-2'>
              <Button type='button' onClick={saveStryd} disabled={strydLoading} variant='default'>
                {strydLoading ? <Loader2 className='h-4 w-4 animate-spin' /> : t('services.strydSave')}
              </Button>
              {strydConnected && (
                <>
                  <Button
                    type='button'
                    onClick={syncStrydActivities}
                    disabled={strydLoading || strydSyncLoading}
                    variant='secondary'
                  >
                    {strydSyncLoading ? (
                      <Loader2 className='h-4 w-4 animate-spin' />
                    ) : (
                      t('services.strydSyncActivities')
                    )}
                  </Button>
                  <Button
                    type='button'
                    onClick={disconnectStryd}
                    disabled={strydLoading}
                    variant='outline'
                  >
                    {t('services.strydDisconnect')}
                  </Button>
                </>
              )}
            </div>
            {strydConnected && (
              <p className='text-xs text-muted-foreground'>{t('services.strydSyncActivitiesHint')}</p>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ServicesModal;
